/** Minimal SQL.js surface. Supplying the WASM factory keeps this module bundler agnostic. */
export interface SqlJsDatabase {
  run(sql: string, params?: unknown[] | Record<string, unknown>): void;
  exec(sql: string, params?: unknown[]): Array<{
    columns: string[];
    values: unknown[][];
  }>;
  export(): Uint8Array;
  close(): void;
}

export interface SqlJsStatic {
  Database: new (data?: Uint8Array) => SqlJsDatabase;
}

export interface DatabasePersistence {
  load(): Promise<Uint8Array | undefined>;
  save(data: Uint8Array): Promise<void>;
}

export class OpfsPersistence implements DatabasePersistence {
  constructor(private readonly filename = "u365-cloudpos.sqlite") {}

  async load(): Promise<Uint8Array | undefined> {
    const root = await navigator.storage.getDirectory();
    try {
      const handle = await root.getFileHandle(this.filename);
      return new Uint8Array(await (await handle.getFile()).arrayBuffer());
    } catch (error) {
      if ((error as DOMException).name === "NotFoundError") return undefined;
      throw error;
    }
  }

  async save(data: Uint8Array): Promise<void> {
    const root = await navigator.storage.getDirectory();
    const handle = await root.getFileHandle(this.filename, { create: true });
    const writer = await handle.createWritable();
    await writer.write(data.slice().buffer as ArrayBuffer);
    await writer.close();
  }
}

export class IndexedDbPersistence implements DatabasePersistence {
  constructor(
    private readonly databaseName = "u365-cloudpos-offline",
    private readonly key = "main.sqlite",
  ) {}

  private open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.databaseName, 1);
      request.onupgradeneeded = () => request.result.createObjectStore("files");
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async load(): Promise<Uint8Array | undefined> {
    const db = await this.open();
    try {
      return await new Promise((resolve, reject) => {
        const request = db.transaction("files").objectStore("files").get(this.key);
        request.onsuccess = () => {
          const value = request.result;
          resolve(value ? new Uint8Array(value as ArrayBuffer) : undefined);
        };
        request.onerror = () => reject(request.error);
      });
    } finally {
      db.close();
    }
  }

  async save(data: Uint8Array): Promise<void> {
    const db = await this.open();
    try {
      await new Promise<void>((resolve, reject) => {
        const transaction = db.transaction("files", "readwrite");
        transaction.objectStore("files").put(data.slice().buffer, this.key);
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
      });
    } finally {
      db.close();
    }
  }
}

export class MemoryPersistence implements DatabasePersistence {
  private data?: Uint8Array;
  async load(): Promise<Uint8Array | undefined> { return this.data?.slice(); }
  async save(data: Uint8Array): Promise<void> { this.data = data.slice(); }
}

export function bestAvailablePersistence(): DatabasePersistence {
  if (typeof navigator !== "undefined" && "storage" in navigator && "getDirectory" in navigator.storage) {
    return new OpfsPersistence();
  }
  if (typeof indexedDB !== "undefined") return new IndexedDbPersistence();
  return new MemoryPersistence();
}

export class OfflineSqliteDatabase {
  private tail: Promise<unknown> = Promise.resolve();

  private constructor(
    readonly raw: SqlJsDatabase,
    private readonly persistence: DatabasePersistence,
  ) {}

  static async open(sql: SqlJsStatic, persistence = bestAvailablePersistence()): Promise<OfflineSqliteDatabase> {
    const database = new OfflineSqliteDatabase(new sql.Database(await persistence.load()), persistence);
    database.migrate();
    await database.persist();
    return database;
  }

  private migrate(): void {
    const statements = [
      "PRAGMA foreign_keys = ON",
      "CREATE TABLE IF NOT EXISTS metadata (key TEXT PRIMARY KEY, value TEXT NOT NULL)",
      "CREATE TABLE IF NOT EXISTS sales (id TEXT PRIMARY KEY, terminal_id TEXT NOT NULL, employee_id TEXT, currency TEXT NOT NULL, subtotal_minor INTEGER NOT NULL, tax_minor INTEGER NOT NULL, total_minor INTEGER NOT NULL, created_at TEXT NOT NULL, synced_at TEXT)",
      "CREATE TABLE IF NOT EXISTS sale_items (id TEXT PRIMARY KEY, sale_id TEXT NOT NULL REFERENCES sales(id) ON DELETE CASCADE, product_id TEXT, name TEXT NOT NULL, quantity_milli INTEGER NOT NULL, unit_price_minor INTEGER NOT NULL, tax_minor INTEGER NOT NULL)",
      "CREATE TABLE IF NOT EXISTS payments (id TEXT PRIMARY KEY, sale_id TEXT NOT NULL REFERENCES sales(id) ON DELETE CASCADE, method TEXT NOT NULL, amount_minor INTEGER NOT NULL, reference TEXT, card_scheme TEXT, card_last4 TEXT, authorization_code TEXT, retrieval_reference_number TEXT, uti TEXT, terminal_id TEXT, merchant_id TEXT, receipt_text TEXT)",
      "CREATE TABLE IF NOT EXISTS outbox (id TEXT PRIMARY KEY, entity_type TEXT NOT NULL, entity_id TEXT NOT NULL, operation TEXT NOT NULL, payload TEXT NOT NULL, created_at TEXT NOT NULL, attempts INTEGER NOT NULL DEFAULT 0, last_error TEXT, blocked INTEGER NOT NULL DEFAULT 0)",
      "CREATE INDEX IF NOT EXISTS outbox_created_idx ON outbox(created_at)",
      "CREATE TABLE IF NOT EXISTS server_entities (entity_type TEXT NOT NULL, entity_id TEXT NOT NULL, revision INTEGER NOT NULL, updated_at TEXT NOT NULL, deleted INTEGER NOT NULL DEFAULT 0, data TEXT, PRIMARY KEY(entity_type, entity_id))",
    ];
    for (const statement of statements) this.raw.run(statement);
    const outboxColumns = rows(this.raw, "PRAGMA table_info(outbox)").map((column) => String(column.name));
    if (!outboxColumns.includes("blocked")) this.raw.run("ALTER TABLE outbox ADD COLUMN blocked INTEGER NOT NULL DEFAULT 0");
    const paymentColumns = new Set(rows(this.raw, "PRAGMA table_info(payments)").map((column) => String(column.name)));
    const cardColumns = ["card_scheme", "card_last4", "authorization_code", "retrieval_reference_number", "uti", "terminal_id", "merchant_id", "receipt_text"];
    for (const column of cardColumns) if (!paymentColumns.has(column)) this.raw.run(`ALTER TABLE payments ADD COLUMN ${column} TEXT`);
  }

  async transaction<T>(work: (database: SqlJsDatabase) => T | Promise<T>): Promise<T> {
    const execute = async (): Promise<T> => {
      this.raw.run("BEGIN IMMEDIATE");
      try {
        const result = await work(this.raw);
        this.raw.run("COMMIT");
        await this.persist();
        return result;
      } catch (error) {
        this.raw.run("ROLLBACK");
        throw error;
      }
    };
    const result = this.tail.then(execute, execute);
    this.tail = result.then(() => undefined, () => undefined);
    return result;
  }

  async persist(): Promise<void> { await this.persistence.save(this.raw.export()); }
  close(): void { this.raw.close(); }
}

export function rows(database: SqlJsDatabase, sql: string, params: unknown[] = []): Record<string, unknown>[] {
  const result = database.exec(sql, params)[0];
  if (!result) return [];
  return result.values.map((values) => Object.fromEntries(result.columns.map((column, index) => [column, values[index]])));
}
